import { getOwner, setOwner } from '@ember/application';
import CampaignModel from 'cio/models/campaign';
import VisualWorkflowBuilderV2 from 'cio/components/visual-workflow-builder-v2';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import {
  ADD_ACTIONS_PANEL_WIDTH,
  ADD_ACTIONS_PANEL_OFFSET,
  KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY,
} from 'cio/utils/visual-workflow-builder';
import { storageFor } from 'ember-local-storage';
import clamp from 'cio/utils/clamp';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { VWBComponentWrapper } from 'cio/utils/visual-workflow-builder/context';

const WHEEL_ZOOM_AMOUNT = 15;

const ZOOM_VALUES = [5, 10, 15, 25, 50, 75, 100, 125, 150, 200];

let findClosestValueIndex = (arr: number[], val: number) => {
  let closestValue = findClosestValue(arr, val);

  return arr.indexOf(closestValue);
};

let findClosestValue = (arr: number[], val: number) => {
  return arr.reduce((prev, curr) => {
    return Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev;
  });
};

export function createZoomManagerForComponent(vwbComponent: VisualWorkflowBuilderV2) {
  let wrapper = new VWBComponentWrapper(vwbComponent);
  let owner = getOwner(vwbComponent);
  return new ZoomManager(wrapper, owner);
}

export interface ZoomManagerParams {
  vwbComponent: VisualWorkflowBuilderV2;

  campaign: CampaignModel;
  tab: VWBTab;
  canvas: HTMLDivElement | null;
  isShowingCopyFromModal: boolean;
  isShowingCopyToModal: boolean;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  initialZoomLevel?: number;
  minZoomLevel?: number;
  maxZoomLevel?: number;
  pathStrokeWidth: number;
  setPathStrokeWidth: (value: number) => void;
}
export class ZoomManager {
  @service analytics!: AnalyticsService;

  @storageFor('cio-alerts') localStorage!: any;

  params: ZoomManagerParams;

  constructor(params: ZoomManagerParams, owner: any) {
    this.params = params;

    setOwner(this, owner);

    if (params.initialZoomLevel != null) {
      this.zoomLevel = params.initialZoomLevel;
    }
  }

  @action
  registerCanvasEvents() {
    this.params.canvas?.addEventListener('wheel', this.onWheel, { passive: false });

    registerDestructor(this.params.vwbComponent, () => {
      this.params.canvas?.removeEventListener('wheel', this.onWheel);
    });
  }

  @tracked zoomLevel = 100;

  get zoomScale() {
    return this.zoomLevel / 100;
  }

  @action
  onWheel(event: WheelEvent) {
    let { canvas, isShowingCopyFromModal, isShowingCopyToModal, offsetX, offsetY } = this.params;
    if (!(event.metaKey || event.ctrlKey) || canvas == null || isShowingCopyFromModal || isShowingCopyToModal) {
      return;
    }

    let canvasRect = canvas.getBoundingClientRect();
    if (
      event.clientX < canvasRect.x ||
      event.clientX > canvasRect.x + canvasRect.width ||
      event.clientY < canvasRect.y ||
      event.clientY > canvasRect.y + canvasRect.height
    ) {
      return;
    }

    event.preventDefault();

    let delta = Math.max(-1, Math.min(1, event.deltaY)) * WHEEL_ZOOM_AMOUNT;

    let targetZoomLevel = Math.min(
      Math.max(this.zoomLevel - delta, ZOOM_VALUES[0]),
      ZOOM_VALUES[ZOOM_VALUES.length - 1],
    );

    this.localStorage.set(KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY, true);
    requestAnimationFrame(() => {
      this.setZoom(targetZoomLevel, {
        targetCoordinates: {
          x: event.clientX - canvasRect.x - offsetX,
          y: event.clientY - canvasRect.y - offsetY,
        },
        debounceTrack: true,
        zoomSource: 'scroll',
      });
    });
  }

  @action
  zoomIn(zoomSource: 'button' | 'scroll' | 'keyboard' = 'button') {
    let closestDefinedLevelIndex = findClosestValueIndex(ZOOM_VALUES, this.zoomLevel);

    let nextZoomLevelIndex = closestDefinedLevelIndex + 1;

    // If the closest defined zoom level is higher than the current one,
    // choose that instead, so that we don't skip a value.
    if (ZOOM_VALUES[closestDefinedLevelIndex] > this.zoomLevel) {
      nextZoomLevelIndex = closestDefinedLevelIndex;
    }

    nextZoomLevelIndex = clamp(nextZoomLevelIndex, ZOOM_VALUES.length - 1, 0);

    this.setZoom(ZOOM_VALUES[nextZoomLevelIndex], { zoomSource });
  }

  @action
  zoomOut(zoomSource: 'button' | 'scroll' | 'keyboard' = 'button') {
    let closestDefinedLevelIndex = findClosestValueIndex(ZOOM_VALUES, this.zoomLevel);

    let nextZoomLevelIndex = closestDefinedLevelIndex - 1;

    // If the closest defined zoom level is lower than the current one, choose
    // that instead, so that we don't skip a value.
    if (ZOOM_VALUES[closestDefinedLevelIndex] < this.zoomLevel) {
      nextZoomLevelIndex = closestDefinedLevelIndex;
    }

    nextZoomLevelIndex = clamp(nextZoomLevelIndex, ZOOM_VALUES.length - 1, 0);

    this.setZoom(ZOOM_VALUES[nextZoomLevelIndex], { zoomSource });
  }

  @action
  zoomToFit(options: { skipTrack?: boolean } = {}) {
    let { canvas, width, height, offsetX, offsetY } = this.params;
    if (canvas == null) {
      return;
    }

    // Decrease available width by size of add actions panel. This way the
    // zoomed-to-fit workflow will fit entirely in the section next to the panel
    let availableWidth = canvas.clientWidth - (ADD_ACTIONS_PANEL_WIDTH + 2 * ADD_ACTIONS_PANEL_OFFSET);
    let availableHeight = canvas.clientHeight;

    let widthScale = availableWidth / width;
    let heightScale = availableHeight / height;

    let isWidthScaleSmaller = widthScale < heightScale;
    let neededScale = Math.min(widthScale, heightScale) * 100;

    this.setZoom(neededScale, { zoomToFit: true, ...options });

    if (isWidthScaleSmaller) {
      // Center the workflow vertically, and center horizontally between add
      // actions panel and right browser edge
      let newScrollHeight = height * (neededScale / 100) + offsetY * 2;
      canvas.scrollTop = (newScrollHeight - canvas.offsetHeight) / 2;
      canvas.scrollLeft = offsetX - (ADD_ACTIONS_PANEL_WIDTH + 2 * ADD_ACTIONS_PANEL_OFFSET);
    } else {
      // This centers the workflow vertically, but the workflow will be as tall
      // as the VWB, so it's enough to set scrollTop to offsetY.
      // Center workflow horizontally, disregarding add actions panel
      let newScrollWidth = width * (neededScale / 100) + offsetX * 2;
      canvas.scrollTop = offsetY;
      canvas.scrollLeft = (newScrollWidth - canvas.offsetWidth) / 2;
    }
  }

  @action
  zoomToValue(value: number, options?: { skipTrack?: boolean }) {
    this.setZoom(value, options);
  }

  @action
  private setZoom(
    value: number,
    options?: {
      skipTrack?: boolean;
      targetCoordinates?: { x: number; y: number };
      zoomToFit?: boolean;
      debounceTrack?: boolean;
      zoomSource?: 'button' | 'scroll' | 'keyboard' | null;
    },
  ) {
    let { canvas, width, height, offsetX, offsetY, minZoomLevel, maxZoomLevel } = this.params;

    if (this.zoomLevel === value) {
      return;
    }

    if (canvas == null) {
      return;
    }

    if (minZoomLevel != null && value < minZoomLevel) {
      return;
    }

    if (maxZoomLevel != null && value > maxZoomLevel) {
      return;
    }

    let pctX = canvas.scrollLeft / (canvas.scrollWidth - canvas.offsetWidth);
    let pctY = canvas.scrollTop / (canvas.scrollHeight - canvas.offsetHeight);

    let originalScale = this.zoomLevel / 100;
    let targetScale = value / 100;

    this.zoomLevel = value;

    let newScrollWidth = width * targetScale + offsetX * 2;
    let newScrollHeight = height * targetScale + offsetY * 2;

    if (options?.targetCoordinates) {
      let offset = {
        x: canvas.scrollLeft,
        y: canvas.scrollTop,
      };
      let targetCoordinatesInCanvas = {
        x: options.targetCoordinates.x + offset.x,
        y: options.targetCoordinates.y + offset.y,
      };
      let unscaledTargetCoordinates = {
        x: targetCoordinatesInCanvas.x / originalScale,
        y: targetCoordinatesInCanvas.y / originalScale,
      };
      let targetCoordinatesInNewScale = {
        x: unscaledTargetCoordinates.x * targetScale,
        y: unscaledTargetCoordinates.y * targetScale,
      };

      canvas.scrollLeft = targetCoordinatesInNewScale.x - options.targetCoordinates.x;
      canvas.scrollTop = targetCoordinatesInNewScale.y - options.targetCoordinates.y;
    } else {
      canvas.scrollLeft = (newScrollWidth - canvas.offsetWidth) * pctX;
      canvas.scrollTop = (newScrollHeight - canvas.offsetHeight) * pctY;
    }

    // If we're zooming out, we adjust the thickness of the path lines so that
    // they're still visible. Otherwise in a scaled container they turn into
    // fractions of a pixel, and basically disappear.
    if (targetScale < 1) {
      // 1/targetScale would produce a line that always looks like it's 1px
      // wide. When zoomed out those stood out too much visually, so we divide
      // the value by 2.
      this.params.setPathStrokeWidth(Math.round(1 / targetScale / 2));
    } else {
      this.params.setPathStrokeWidth(1);
    }

    if (options?.skipTrack) {
      return;
    }
    this.trackZoom.perform(
      value,
      options?.zoomToFit ?? false,
      options?.zoomSource ?? 'button',
      options?.debounceTrack ?? false,
    );
  }

  trackZoom = task(
    { restartable: true },
    async (
      scale: number,
      zoomToFit: boolean,
      zoomSource: 'button' | 'scroll' | 'keyboard' | null = 'button',
      debounce = false,
    ) => {
      let { campaign, tab } = this.params;

      if (debounce) {
        await timeout(config.APP.shortDebounceDuration);
      }

      this.analytics.track('campaign', 'workflow_canvas_zoom_changed', {
        model: campaign,
        scale,
        zoomToFit,
        trayTab: tab ?? VWBTab.Build,
        controlType: (zoomSource ?? 'button') as string,
      });
    },
  );
}
